import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import { formatDateOnly, formatOrgDateTime } from '@/lib/utils/custom-date/custom-date';
import { EmployeesPagingResult, useGetMyEmployee } from '@/modules/hr-directory';
import { User } from '@/types/user.type';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';
import { Button } from '@/components/ui-kit/button';
import { Dialog } from '@/components/ui-kit/dialog';
import { Separator } from '@/components/ui-kit/separator';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { EditProfile } from '../modals/edit-profile/edit-profile';

const DummyProfile = '/assets/images/dummy_profile.png';

interface ProfileCardProps {
  userInfo?: User;
  isLoading: boolean;
  onEditClick: () => void;
  isEditProfileModalOpen: boolean;
  setIsEditProfileModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleEditProfileClose: () => void;
}

export const ProfileCard = ({
  userInfo,
  isLoading,
  onEditClick,
  isEditProfileModalOpen,
  setIsEditProfileModalOpen,
  handleEditProfileClose,
}: Readonly<ProfileCardProps>) => {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const profileImageSrc = useMemo(() => {
    if (!userInfo?.profileImageUrl || userInfo.profileImageUrl === '') {
      return DummyProfile;
    }
    // Add cache busting parameter to force reload when image changes
    const timestamp = new Date().getTime();
    return `${userInfo.profileImageUrl}?t=${timestamp}`;
  }, [userInfo?.profileImageUrl]);

  React.useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [profileImageSrc]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoaded(true);
    setImageError(true);
  };

  // "Joined on" is the HR joining date; the IAM account's creation date only stands in without an HR record.
  const { data: myEmployee, isLoading: isEmployeeLoading } = useGetMyEmployee();
  const joiningDate = (myEmployee as { Employees?: EmployeesPagingResult } | undefined)?.Employees
    ?.items?.[0]?.JoiningDate;
  const joinedOn = joiningDate
    ? formatDateOnly(joiningDate)
    : formatOrgDateTime(userInfo?.createdDate) || '-';

  return (
    <Card className="w-full border-none rounded-[8px] shadow-sm">
      <CardHeader className="p-0 hidden">
        <CardTitle />
        <CardDescription />
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <div className="flex justify-between gap-2">
          <div className="flex min-w-0 items-center">
            <div className="relative h-16 w-16 shrink-0 rounded-full border overflow-hidden border-white shadow-sm">
              {isLoading ? (
                <Skeleton className="w-16 h-16 rounded-full" />
              ) : (
                <>
                  {!imageLoaded && !imageError && (
                    <Skeleton className="w-16 h-16 rounded-full absolute inset-0 z-10" />
                  )}
                  <img
                    key={profileImageSrc}
                    src={imageError ? DummyProfile : profileImageSrc}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                  />
                </>
              )}
            </div>
            <div className="flex min-w-0 flex-col gap-1 ml-3 sm:ml-9">
              {isLoading ? (
                <>
                  <Skeleton className="w-40 h-5" />
                  <Skeleton className="w-48 h-4 mt-1" />
                </>
              ) : (
                <>
                  <h1 className="text-xl text-high-emphasis font-semibold break-words">
                    {userInfo?.firstName} {userInfo?.lastName}
                  </h1>
                  <p className="text-sm text-medium-emphasis break-all">{userInfo?.email}</p>
                </>
              )}
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="shrink-0 text-[#0161AB] hover:bg-[#0161AB]/5 hover:text-[#0161AB]"
            onClick={onEditClick}
          >
            <Pencil className="h-3 w-3" />
            <span className="sr-only text-sm font-bold sm:not-sr-only sm:whitespace-nowrap">
              {t('EDIT')}
            </span>
          </Button>
          <Dialog open={isEditProfileModalOpen} onOpenChange={setIsEditProfileModalOpen}>
            {userInfo && <EditProfile userInfo={userInfo} onClose={handleEditProfileClose} />}
          </Dialog>
        </div>
        <Separator orientation="horizontal" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 [&>div]:min-w-0 [&_p]:break-words">
          {isLoading ? (
            ['mobile', 'joined', 'last-logged-in'].map((key) => (
              <div key={key}>
                <Skeleton className="h-3 w-20 mb-1" />
                <Skeleton className="h-5 w-36" />
              </div>
            ))
          ) : (
            <>
              <div>
                <p className="text-medium-emphasis text-xs font-normal">{t('MOBILE_NO')}</p>
                <p className="text-high-emphasis text-sm">{userInfo?.phoneNumber ?? '-'}</p>
              </div>
              <div>
                <p className="text-medium-emphasis text-xs font-normal">{t('JOINED_ON')}</p>
                {/* Not the account's creation date while the HR joining date is loading. */}
                {isEmployeeLoading ? (
                  <Skeleton className="mt-1 h-4 w-24" />
                ) : (
                  <p className="text-high-emphasis text-sm">{joinedOn}</p>
                )}
              </div>
              <div>
                <p className="text-medium-emphasis text-xs font-normal">{t('LAST_LOGGED_IN')}</p>
                <p className="text-high-emphasis text-sm">
                  {formatOrgDateTime(userInfo?.lastLoggedInTime) || '-'}
                </p>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
